import api from './api-service';

export const GET_PREFERENCES_QUERY = `
query getPreferences {
	session {
		id
		emailNormal
		preferences
	}
}
`;

export const UPDATE_PREFERENCES_MUTATION = `
mutation updatePreferences($input: UserInput) {
	updateSession(input: $input) {
		session {
			id
			preferences
		}
	}
}
`;

export const UPDATE_USER_MUTATION = `
mutation updateUser($input: UserInput) {
  updateUser(input: $input) {
    user {
      id
      name
    }
  }
}
`;

export const SEARCH_USERS_QUERY = `
query searchUsers($q: String) {
  users(q: $q, limit: 5) {
    nodes {
      id
      name
      emailNormal
    }
  }
}
`;

export const GET_ALL_USERS_QUERY = `
query getAllUsers($offset: Int, $limit: Int, $search: String, $approved: Boolean, $registered: Boolean) {
  users(offset: $offset, limit: $limit, q: $search, approved: $approved, registered: $registered) {
  	totalCount
    nodes {      
      id
      name
      emailNormal
      territory {
        id
        code
        name
	  }
      status
      accountType
      username
      groups
	  created
    }
  }
}
`;

export const SEARCH_GROUPS_QUERY = `
query searchGroups($q: String) {
  groups(q: $q, limit: 5) {
    nodes {
      id
      name
    }
  }
}
`;

export const GET_USERS_BY_ID_QUERY = `
query getUsersById($id: [ID]) {
  users(id: $id) {
    nodes {
		id
		name
      	emailNormal
		territory {
			name
		}
    }
  }
}
`;

export const GET_GROUPS_BY_ID_QUERY = `
query getGroupsById($id: [ID]) {
  groups(id: $id) {
    nodes {
			id
			name
    }
  }
}
`;

const UserService = {
	isAdmin(user) {
		return (
			user?.groups?.some((group) => group.type === 'a')
		);
	},

	isAdminManager(user) {
		return user?.permissions?.some((permission) => {
			return permission.access && permission.access.adminWorkRequests === true;
		});
	},

	isVendor(user) {
		return user?.permissions?.some((permission) => {
			return permission.access && permission.access.manageWorkRequests === true;
		});
	},

	// The user is a Territory user if he's the owner of the work request or if he's a member of the owner's group
	isTerritory(user, workRequest) {
		const sessionUserId = user?.id ?? user?._id;
		return !!(sessionUserId === workRequest?.from?._id
			|| workRequest?.from?.groups?.some((ownerGroup) => user?.groups?.some(
				(userGroup) => (userGroup?._id ?? userGroup?.id) === (ownerGroup?._id ?? ownerGroup?.id)
			)));
	},

	isUser(user) {
		return !!(user && (user.id ?? user._id)) && !(UserService.isAdmin(user) || UserService.isVendor(user));
	},

	isGuest(user) {
		return user?.accountType === 'guest' || false;
	},

	hasReportAccess(user, report) {
		if( UserService.isAdmin(user) ) {
			return true;
		}

		// checking for access to an individual report
		if( report ) {
			return user.permissions.some((permission) => permission.access?.[report] === true);
		}

		// checking for any report access (to show menu button
		// and allow access to /reports)
		const accessKeys = [
			'workRequestReport',
			'groupSpendingReport',
			'exceptionReport',
			'downloadReport',
			'feedbackReport',
			'encodeReport',
			'shareReport'
		];

		return accessKeys.some((key) => {
			return user.permissions.some((permission) => permission.access?.[key] === true);
		});
	},

	getPreferences() {
		return api.graphql.request(GET_PREFERENCES_QUERY);
	},

	updatePreferences(id, preferences) {
		return api.graphql.request(UPDATE_PREFERENCES_MUTATION, {
			input: {
				id,
				preferences
			}
		});
	},

	updateUser(id, data) {
		return api.graphql.request(UPDATE_USER_MUTATION, {
			input: {
				...data,
				id
			}
		});
	},

	patchUser(data) {
		return api.graphql.request(UPDATE_USER_MUTATION, {
			input: data
		});
	},

	createUser(data) {
		return api.client.post('/users', data);
	},

	sendGuestLoginLink(id) {
		return api.client.post(`/users/${id}/guest-invite`, {
			invite: `${window.location.origin}/register/:code`
		}, { validateStatus: () => true }).then(({ data, status }) => {
			if( status !== 200 ) {
				throw new Error(data?.error?.message || data?.error || data?.message || 'Could not send a guest link right now');
			}
			return data;
		});
	},

	getUsers(query) {
		return api.graphql.request(SEARCH_USERS_QUERY, { q: query });
	},

	getAllUsers(params) {
		if( !params.search ) {
			delete params.search;
		}
		return api.graphql.request(GET_ALL_USERS_QUERY, params).then((res) => res.users);
	},

	getGroups(query) {
		return api.graphql.request(SEARCH_GROUPS_QUERY, { q: query });
	},

	getUsersByIds(userIds) {
		return api.graphql.request(GET_USERS_BY_ID_QUERY, { id: userIds });
	},

	getGroupsByIds(groupIds) {
		return api.graphql.request(GET_GROUPS_BY_ID_QUERY, { id: groupIds });
	},
	getUserByEmail(email) {
		return api.client.get(`/users?q=${encodeURIComponent(email)}`, { headers: { 'X-Range': 'resources=0-9' } })
			.then((res) => res?.data?.[0] || res?.data);
	},
	deleteUser(id) {
		return api.client.delete(`/users/${typeof id === 'object' ? id.id : id}`);
	}
};

export default UserService;
